/**
 * React Hook for Notification Management
 * 
 * Provides notification functionality for components
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { NotificationService } from '../services/NotificationService';
import { NotificationServiceV2 } from '../services/NotificationServiceV2';
import { OneSignalService } from '../services/OneSignalService';
import { playNotificationSound, preloadNotificationSound } from '../utils/notificationSound';
import { 
  UserNotificationPreferences, 
  BaseNotification, 
  NotificationChannel,
  FCMToken,
  AppNotification
} from '../@types/notification';

export interface UseNotificationsReturn {
  // State
  preferences: UserNotificationPreferences | null;
  fcmToken: string | null;
  isSupported: boolean;
  isLoading: boolean;
  error: string | null;
  // V2 notifications
  notifications: AppNotification[];
  unreadCount: number;

  // Actions
  updatePreferences: (preferences: Partial<UserNotificationPreferences>) => Promise<void>;
  registerFCMToken: () => Promise<void>;
  sendTestNotification: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  clearError: () => void;
}

export const useNotifications = (): UseNotificationsReturn => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserNotificationPreferences | null>(null);
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // V2 notification state
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const notificationService = NotificationService.getInstance();
  const oneSignalService = OneSignalService.getInstance();

  /**
   * Check if push notifications are supported
   */
  const checkSupport = useCallback(() => {
    if (typeof window !== 'undefined') {
      setIsSupported(
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
      );
    }
  }, []);

  /**
   * Load user preferences
   */
  const loadPreferences = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const userPreferences = await notificationService.getUserPreferences(user.id);
      setPreferences(userPreferences);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, notificationService]);

  /**
   * Update user preferences
   */
  const updatePreferences = useCallback(async (newPreferences: Partial<UserNotificationPreferences>) => {
    if (!user?.id) return;

    try {
      await notificationService.updateUserPreferences(user.id, newPreferences);
      setPreferences(prev => prev ? { ...prev, ...newPreferences } : null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [user?.id, notificationService]);

  /**
   * Register FCM token for push notifications
   */
  const registerFCMToken = useCallback(async () => {
    if (!user?.id || !isSupported) return;

    try {
      await oneSignalService.initialize();
      const permission = await oneSignalService.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }

      await oneSignalService.login(user.id);
      const subscriptionId = await oneSignalService.getPushSubscriptionId();

      if (!subscriptionId) {
        throw new Error('Unable to retrieve OneSignal subscription ID');
      }

      await notificationService.registerFCMToken(user.id, subscriptionId, 'web');
      setFcmToken(subscriptionId);

      console.log('📱 OneSignal push subscription registered:', subscriptionId);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [user?.id, isSupported, notificationService, oneSignalService]);

  /**
   * Send test notification
   */
  const sendTestNotification = useCallback(async () => {
    if (!user?.id) return;

    try {
      await notificationService.createSystemAlert(
        'Test Notification',
        'This is a test notification from McKenzie Portal',
        'normal',
        user.id
      );
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [user?.id, notificationService]);

  /**
   * Fetch V2 notifications (including read notifications)
   */
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      // Fetch all notifications (read and unread) - don't filter by read status
      const userNotifications = await NotificationServiceV2.getUserNotifications(user.id);
      // Sort by createdAt descending (newest first) to ensure correct order
      const sortedNotifications = [...userNotifications].sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt?.toDate?.()?.getTime() || 0;
        const bTime = b.createdAt?.toMillis?.() || b.createdAt?.toDate?.()?.getTime() || 0;
        return bTime - aTime; // Descending order (newest first)
      });
      setNotifications(sortedNotifications);

      const count = await NotificationServiceV2.getUnreadCount(user.id);
      setUnreadCount(count);
    } catch (err: any) {
      console.error('Error fetching notifications:', err);
    }
  }, [user?.id]);

  /**
   * Mark notification as read (V2)
   */
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user?.id) return;

    try {
      // Check if notification is currently unread before marking as read
      const notification = notifications.find(n => n.id === notificationId);
      const wasUnread = notification && !notification.read;

      await NotificationServiceV2.markAsRead(notificationId, user.id);
      
      // Update local state and maintain sort order
      setNotifications(prev => {
        const updated = prev.map(n => 
          n.id === notificationId 
            ? { ...n, read: true, readAt: Timestamp.now() }
            : n
        );
        // Sort by createdAt descending (newest first)
        return updated.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt?.toDate?.()?.getTime() || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt?.toDate?.()?.getTime() || 0;
          return bTime - aTime; // Descending order (newest first)
        });
      });
      
      // Only decrease unread count if it was unread
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to mark notification as read');
      throw err;
    }
  }, [user?.id, notifications]);

  /**
   * Mark all notifications as read (V2)
   */
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;

    try {
      await NotificationServiceV2.markAllAsRead(user.id);
      
      // Update local state and maintain sort order
      setNotifications(prev => {
        const updated = prev.map(n => ({ ...n, read: true, readAt: Timestamp.now() }));
        // Sort by createdAt descending (newest first)
        return updated.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt?.toDate?.()?.getTime() || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt?.toDate?.()?.getTime() || 0;
          return bTime - aTime; // Descending order (newest first)
        });
      });
      
      setUnreadCount(0);
    } catch (err: any) {
      setError(err.message || 'Failed to mark all notifications as read');
      throw err;
    }
  }, [user?.id]);

  /**
   * Delete notification (V2)
   */
  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!user?.id) return;

    try {
      await NotificationServiceV2.deleteNotification(notificationId, user.id);
      
      // Update local state
      const deletedNotification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete notification');
      throw err;
    }
  }, [user?.id, notifications]);

  /**
   * Refresh notifications
   */
  const refreshNotifications = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Set up real-time listener for V2 notifications
   */
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = NotificationServiceV2.subscribeToNotifications(
      user.id,
      (notification: AppNotification) => {
        // Update or add notification to the list
        let isNewNotification = false;
        setNotifications(prev => {
          // Check if notification already exists
          const existingIndex = prev.findIndex(n => n.id === notification.id);
          let updated: AppNotification[];
          if (existingIndex >= 0) {
            // Update existing notification (e.g., when marked as read)
            updated = [...prev];
            updated[existingIndex] = notification;
            isNewNotification = false;
          } else {
            // Add new notification
            updated = [notification, ...prev].slice(0, 50);
            isNewNotification = true;
          }
          // Sort by createdAt descending (newest first) to ensure correct order
          return updated.sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || a.createdAt?.toDate?.()?.getTime() || 0;
            const bTime = b.createdAt?.toMillis?.() || b.createdAt?.toDate?.()?.getTime() || 0;
            return bTime - aTime; // Descending order (newest first)
          });
        });
        
        // Update unread count if notification is unread
        if (!notification.read) {
          setUnreadCount(prev => prev + 1);
          
          // Play notification sound for new unread notifications
          if (isNewNotification) {
            playNotificationSound(0.5);
          }
        } else {
          // If notification is read, make sure unread count doesn't go negative
          setUnreadCount(prev => Math.max(0, prev));
        }
      }
    );

    // Initial fetch
    fetchNotifications();

    return () => {
      unsubscribe();
    };
  }, [user?.id, fetchNotifications]);

  /**
   * Initialize notifications on mount
   */
  useEffect(() => {
    checkSupport();
    if (user?.id) {
      loadPreferences();
    }
    // Preload notification sound for better performance
    preloadNotificationSound();
  }, [user?.id, checkSupport, loadPreferences]);

  // Ensure notifications are always sorted by createdAt descending (newest first)
  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || a.createdAt?.toDate?.()?.getTime() || 0;
      const bTime = b.createdAt?.toMillis?.() || b.createdAt?.toDate?.()?.getTime() || 0;
      return bTime - aTime; // Descending order (newest first)
    });
  }, [notifications]);

  return {
    // State
    preferences,
    fcmToken,
    isSupported,
    isLoading,
    error,
    notifications: sortedNotifications,
    unreadCount,

    // Actions
    updatePreferences,
    registerFCMToken,
    sendTestNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    clearError
  };
};
